import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createWorkshop,
  getWorkshopDetail,
  getWorkshopImageUploadUrl,
  uploadWorkshopImageToS3,
  updateWorkshop,
} from '../../api/workshop/workshop.api';
import MeetingPointPicker from '../../components/Kpathport/WorkshopCreate/MeetingPointPicker';
import WorkshopImageUploader from '../../components/Kpathport/WorkshopCreate/WorkshopImageUploader';
import { WORKSHOP_CATEGORIES } from '../../components/Kpathport/utils/workshopCategory';
import { RefundPolicyContent } from '../../components/Kpathport/Modal/RefundModal';
import { BaseButton, ToggleButton } from '@repo/common';

const EMPTY_SLOT = {
  date: '',
  startTime: '',
  endTime: '',
  maxParticipants: 10,
};

/** 예약 전 사전 질문. optionsText 는 화면 편집용이고 보낼 때 배열로 바꾼다 */
const EMPTY_QUESTION = {
  label: '',
  type: 'TEXT',
  required: true,
  optionsText: '',
};

const QUESTION_TYPES = [
  { value: 'TEXT', label: 'Short answer' },
  { value: 'YES_NO', label: 'Yes / No' },
  { value: 'CHOICE', label: 'Multiple choice' },
];

const INPUT_CLASS =
  'w-full rounded-lg border border-grayscale-gray03 bg-white px-4 py-3 typo-b14 text-text-primary outline-none transition focus:border-green-deep02';
const LABEL_CLASS = 'mb-2 block typo-b14Strong text-text-primary';

const WorkshopCreate = () => {
  const navigate = useNavigate();
  const { workshopId } = useParams();
  const isEditMode = Boolean(workshopId);
  const [form, setForm] = useState({
    category: 'CULTURE',
    title: '',
    description: '',
    details: '',
    inquiry: '',
    inquiryPhone: '',
    inquiryEmail: '',
    inquiryOpenChatId: '',
    location: '',
    meetingPointName: '',
    meetingPointAddress: '',
    meetingPointPlaceId: '',
    meetingPointLatitude: null,
    meetingPointLongitude: null,
    price: '',
    currency: 'USD',
    thingsToKnow: '',
    featured: false,
    hit: false,
    free: false,
    keywordsText: '',
    // "View cancellation & refund details" 토글. 꺼져 있으면 기본 Koready 정책 문구가 그대로 쓰인다
    useCustomRefundPolicy: false,
    refundPolicy: '',
    // 결제 방식. BANK_TRANSFER 면 카드 대신 계좌이체 + 어드민 수동 확약 흐름
    paymentMethod: 'CARD',
    bankName: '',
    bankAccountNumber: '',
    bankAccountHolder: '',
    hostNotificationEmail: '',
    confirmationMessage: '',
  });
  const [images, setImages] = useState([]);
  const [dateSlots, setDateSlots] = useState([{ ...EMPTY_SLOT }]);
  // 예약 전 사전 질문. 비워두면 예약 시 모달이 뜨지 않는다
  const [questions, setQuestions] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  useEffect(() => {
    if (!isEditMode) return;

    const loadWorkshop = async () => {
      try {
        const data = await getWorkshopDetail(workshopId);
        setForm({
          category: data.category || 'CULTURE',
          title: data.title || '',
          description: data.description || '',
          details: data.details || '',
          inquiry: data.inquiry || '',
          inquiryPhone: data.inquiryContact?.phone || '',
          inquiryEmail: data.inquiryContact?.email || '',
          inquiryOpenChatId: data.inquiryContact?.openChatId || '',
          location: data.location || '',
          meetingPointName: data.meetingPoint?.name || '',
          meetingPointAddress: data.meetingPoint?.address || '',
          meetingPointPlaceId: data.meetingPoint?.placeId || '',
          meetingPointLatitude: data.meetingPoint?.latitude ?? null,
          meetingPointLongitude: data.meetingPoint?.longitude ?? null,
          price: data.price ?? '',
          currency: data.currency || 'USD',
          thingsToKnow: data.thingsToKnow || '',
          featured: Boolean(data.featured),
          hit: Boolean(data.hit),
          free: Boolean(data.free),
          keywordsText: (data.keywords || []).join(', '),
          useCustomRefundPolicy: Boolean((data.refundPolicy || '').trim()),
          refundPolicy: data.refundPolicy || '',
          paymentMethod: data.paymentMethod || 'CARD',
          bankName: data.bankTransfer?.bankName || '',
          bankAccountNumber: data.bankTransfer?.accountNumber || '',
          bankAccountHolder: data.bankTransfer?.accountHolder || '',
          hostNotificationEmail: data.hostNotificationEmail || '',
          confirmationMessage: data.confirmationMessage || '',
        });
        setImages(
          (data.images || []).map((url, index) => ({
            id: `existing-${index}-${url}`,
            file: null,
            previewUrl: url,
            url,
          })),
        );
        setDateSlots(
          data.dateSlots?.length
            ? data.dateSlots.map((slot) => ({
                id: slot.id,
                date: slot.date,
                startTime: slot.startTime,
                endTime: slot.endTime,
                maxParticipants: slot.maxParticipants,
              }))
            : [{ ...EMPTY_SLOT }],
        );
        setQuestions(
          (data.questions ?? []).map((question) => ({
            id: question.id,
            label: question.label,
            type: question.type || 'TEXT',
            required: Boolean(question.required),
            // 화면에서는 줄바꿈으로 편집하고, 보낼 때 배열로 바꾼다
            optionsText: (question.options ?? []).join('\n'),
          })),
        );
      } catch (error) {
        setSubmitError(
          error.response?.data?.message || 'The workshop could not be loaded.',
        );
      } finally {
        setLoading(false);
      }
    };

    loadWorkshop();
  }, [isEditMode, workshopId]);

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: '',
      ...(name === 'meetingPointName' ? { meetingPoint: '' } : {}),
    }));
  };

  const updateSlot = (index, field, value) => {
    setDateSlots((prev) =>
      prev.map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, [field]: value } : slot,
      ),
    );
    setErrors((prev) => ({ ...prev, dateSlots: '' }));
  };

  const addSlot = () => {
    setDateSlots((prev) => [...prev, { ...EMPTY_SLOT }]);
  };

  const removeSlot = (index) => {
    setDateSlots((prev) => prev.filter((_, slotIndex) => slotIndex !== index));
  };

  /* ── 예약 전 사전 질문 ─────────────────────────────── */

  const updateQuestion = (index, field, value) => {
    setQuestions((prev) =>
      prev.map((question, questionIndex) =>
        questionIndex === index ? { ...question, [field]: value } : question,
      ),
    );
    setErrors((prev) => ({ ...prev, questions: '' }));
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, { ...EMPTY_QUESTION }]);
  };

  const removeQuestion = (index) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.title.trim()) nextErrors.title = 'Enter a workshop title.';
    if (!form.description.trim()) {
      nextErrors.description = 'Enter a short description.';
    }
    if (!form.location.trim()) nextErrors.location = 'Enter a location.';
    if (!form.meetingPointName.trim() || !form.meetingPointAddress.trim()) {
      nextErrors.meetingPoint = 'Select a detailed meeting point.';
    }
    if (!images.length) {
      nextErrors.images = 'Upload at least one workshop image.';
    }
    if (!form.free && (!form.price || Number(form.price) < 0)) {
      nextErrors.price = 'Enter a valid price.';
    }
    if (
      form.inquiryEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.inquiryEmail)
    ) {
      nextErrors.inquiryEmail = 'Enter a valid e-mail address.';
    }
    if (form.paymentMethod === 'BANK_TRANSFER' && !form.free) {
      if (
        !form.bankName.trim() ||
        !form.bankAccountNumber.trim() ||
        !form.bankAccountHolder.trim()
      ) {
        nextErrors.bankTransfer =
          'Enter the bank, account number and account holder for bank transfer.';
      }
    }
    // 라이브 Eximbay 계정은 USD 만 받는다. KRW 카드 워크숍을 만들면 게스트가 결제 단계에서 막힌다
    if (
      !form.free &&
      form.currency === 'KRW' &&
      form.paymentMethod !== 'BANK_TRANSFER'
    ) {
      nextErrors.price =
        'Card payment (Eximbay) only supports USD. For a KRW price, choose Bank transfer as the payment method or price the workshop in USD.';
    }
    if (
      form.hostNotificationEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.hostNotificationEmail)
    ) {
      nextErrors.hostNotificationEmail = 'Enter a valid e-mail address.';
    }
    if (form.useCustomRefundPolicy && !form.refundPolicy.trim()) {
      nextErrors.refundPolicy =
        'Enter the custom refund policy, or turn the toggle off to use the default policy.';
    }
    if (!dateSlots.length) {
      nextErrors.dateSlots = 'Add at least one booking slot.';
    } else if (
      dateSlots.some(
        (slot) =>
          !slot.date ||
          !slot.startTime ||
          !slot.endTime ||
          Number(slot.maxParticipants) < 1 ||
          slot.startTime >= slot.endTime,
      )
    ) {
      nextErrors.dateSlots =
        'Complete every slot and make sure the end time is after the start time.';
    }

    if (questions.some((question) => !question.label.trim())) {
      nextErrors.questions = 'Enter the question text, or remove the question.';
    } else if (
      questions.some(
        (question) =>
          question.type === 'CHOICE' &&
          question.optionsText
            .split('\n')
            .map((option) => option.trim())
            .filter(Boolean).length < 2,
      )
    ) {
      nextErrors.questions =
        'A multiple-choice question needs at least two options.';
    }

    setErrors(nextErrors);

    const firstInvalidField = Object.keys(nextErrors)[0];
    if (firstInvalidField) {
      requestAnimationFrame(() => {
        const field = document.querySelector(
          `[data-validation-field="${firstInvalidField}"]`,
        );
        field?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const focusTarget = field?.matches?.('input, textarea, select')
          ? field
          : field?.querySelector?.('input, textarea, select');
        focusTarget?.focus?.({ preventScroll: true });
      });
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    if (!validate()) return;

    const keywordSeparator = /[,\n]/;
    const payload = {
      category: form.category,
      title: form.title.trim(),
      description: form.description.trim(),
      details: form.details.trim(),
      inquiry: form.inquiry.trim(),
      inquiryPhone: form.inquiryPhone.trim(),
      inquiryEmail: form.inquiryEmail.trim(),
      inquiryOpenChatId: form.inquiryOpenChatId.trim(),
      location: form.location.trim(),
      meetingPointName: form.meetingPointName.trim(),
      meetingPointAddress: form.meetingPointAddress.trim(),
      meetingPointPlaceId: form.meetingPointPlaceId,
      meetingPointLatitude: form.meetingPointLatitude,
      meetingPointLongitude: form.meetingPointLongitude,
      price: form.free ? 0 : Number(form.price),
      currency: form.currency,
      thingsToKnow: form.thingsToKnow.trim(),
      featured: form.featured,
      hit: form.hit,
      free: form.free,
      // 토글이 꺼져 있으면 "" 를 보내 서버가 전용 정책을 지운다(→ 기본 정책). null 은 "미변경" 이라 쓰지 않는다
      refundPolicy: form.useCustomRefundPolicy ? form.refundPolicy.trim() : '',
      paymentMethod: form.free ? 'CARD' : form.paymentMethod,
      bankName: form.bankName.trim(),
      bankAccountNumber: form.bankAccountNumber.trim(),
      bankAccountHolder: form.bankAccountHolder.trim(),
      hostNotificationEmail: form.hostNotificationEmail.trim(),
      confirmationMessage: form.confirmationMessage.trim(),
      keywords: form.keywordsText
        .split(keywordSeparator)
        .map((keyword) => keyword.trim())
        .filter(Boolean),
      dateSlots: dateSlots.map((slot) => ({
        ...slot,
        maxParticipants: Number(slot.maxParticipants),
      })),
      // 항상 보낸다. 빈 배열이면 서버가 기존 질문을 접는다(soft delete)
      questions: questions.map((question) => ({
        id: question.id ?? null,
        label: question.label.trim(),
        type: question.type,
        required: Boolean(question.required),
        options:
          question.type === 'CHOICE'
            ? question.optionsText
                .split('\n')
                .map((option) => option.trim())
                .filter(Boolean)
            : null,
      })),
    };

    try {
      setSubmitting(true);
      const uploadedImageKeys = await Promise.all(
        images.map(async ({ file, url }) => {
          if (!file) return url;
          const { uploadUrl, key } = await getWorkshopImageUploadUrl(file.name);
          await uploadWorkshopImageToS3(file, uploadUrl);
          return key;
        }),
      );

      payload.images = uploadedImageKeys;
      const savedWorkshop = isEditMode
        ? await updateWorkshop(workshopId, payload)
        : await createWorkshop(payload);
      navigate(
        isEditMode
          ? `/admin/workshop/${savedWorkshop.id}`
          : `/community/kpathport/workshop/${savedWorkshop.id}`,
        { replace: true },
      );
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        `The workshop could not be ${
          isEditMode ? 'updated' : 'created'
        }. Please check the fields and try again.`;
      setSubmitError(message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(
      isEditMode
        ? `/admin/workshop/${workshopId}`
        : '/mypage?tab=Workshop%20Management',
    );
  };

  if (loading) {
    return (
      <main className='flex min-h-[60vh] items-center justify-center bg-grayscale-gray01'>
        <p className='typo-b16 text-text-secondary'>Loading workshop...</p>
      </main>
    );
  }

  return (
    <main className='w-full bg-grayscale-gray01 px-8 py-[60px]'>
      <form
        onSubmit={handleSubmit}
        className='mx-auto flex w-full max-w-[1164px] flex-col gap-6'
      >
        <header className='mb-2 flex items-end justify-between'>
          <div>
            <p className='mb-2 typo-b14 text-text-secondary'>Admin only</p>
            <h1 className='typo-h3 text-text-primary'>
              {isEditMode ? 'Edit workshop' : 'Create workshop'}
            </h1>
            <p className='mt-3 typo-b14 text-text-secondary'>
              {isEditMode
                ? 'Update the workshop, booking slots, images, and meeting point.'
                : 'Publish a workshop, booking slots, images, and meeting point.'}
            </p>
          </div>
          <BaseButton
            type='text'
            size='m'
            htmlType='button'
            hideIcon
            typo={false}
            onClick={handleCancel}
            className='h-auto min-w-0 rounded-lg border-0 bg-transparent px-4 py-2 typo-b14Strong text-text-secondary hover:bg-grayscale-gray02 hover:text-text-secondary'
          >
            Back to management
          </BaseButton>
        </header>

        {submitError && (
          <div
            role='alert'
            className='rounded-lg border border-red-300 bg-red-50 px-4 py-3 typo-b14 text-red-700'
          >
            {submitError}
          </div>
        )}

        <section className='rounded-xl bg-white p-8'>
          <h2 className='mb-6 typo-title2 text-text-primary'>Basic details</h2>
          <div className='grid grid-cols-2 gap-5'>
            <label>
              <span className={LABEL_CLASS}>Category</span>
              <select
                name='category'
                value={form.category}
                onChange={updateField}
                className={INPUT_CLASS}
              >
                {WORKSHOP_CATEGORIES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className={LABEL_CLASS}>Title *</span>
              <input
                data-validation-field='title'
                name='title'
                value={form.title}
                onChange={updateField}
                className={INPUT_CLASS}
                placeholder='e.g. Korean cooking class'
                maxLength={200}
              />
              {errors.title && (
                <span className='mt-1 block typo-b12 text-red-600'>
                  {errors.title}
                </span>
              )}
            </label>
          </div>
          <label className='mt-5 block'>
            <span className={LABEL_CLASS}>Short description *</span>
            <textarea
              data-validation-field='description'
              name='description'
              value={form.description}
              onChange={updateField}
              className={`${INPUT_CLASS} min-h-[100px] resize-y`}
              placeholder='A short summary shown on the workshop page'
            />
            {errors.description && (
              <span className='mt-1 block typo-b12 text-red-600'>
                {errors.description}
              </span>
            )}
          </label>
          <label className='mt-5 block'>
            <span className={LABEL_CLASS}>Full details</span>
            <textarea
              name='details'
              value={form.details}
              onChange={updateField}
              className={`${INPUT_CLASS} min-h-[160px] resize-y`}
              placeholder='Schedule, inclusions, preparation, and other details'
            />
          </label>
        </section>

        <section className='rounded-xl bg-white p-8'>
          <h2 className='mb-6 typo-title2 text-text-primary'>
            Location & meeting point
          </h2>
          <div data-validation-field='location'>
            <span data-validation-field='meetingPoint' className='sr-only' />
            <MeetingPointPicker
              meetingPoint={{
                name: form.meetingPointName,
                address: form.meetingPointAddress,
              }}
              errors={errors}
              onClear={() => {
                setForm((prev) => ({
                  ...prev,
                  location: '',
                  meetingPointName: '',
                  meetingPointAddress: '',
                  meetingPointPlaceId: '',
                  meetingPointLatitude: null,
                  meetingPointLongitude: null,
                }));
              }}
              onSelect={(place) => {
                setForm((prev) => ({
                  ...prev,
                  location: place.location,
                  meetingPointName: place.name,
                  meetingPointAddress: place.address,
                  meetingPointPlaceId: place.placeId,
                  meetingPointLatitude: place.latitude,
                  meetingPointLongitude: place.longitude,
                }));
                setErrors((prev) => ({
                  ...prev,
                  location: '',
                  meetingPoint: '',
                }));
              }}
            />
          </div>

          {form.meetingPointAddress && (
            <div className='mt-5 grid grid-cols-2 gap-5'>
              <label>
                <span className={LABEL_CLASS}>
                  Meeting point instructions *
                </span>
                <input
                  name='meetingPointName'
                  value={form.meetingPointName}
                  onChange={updateField}
                  className={INPUT_CLASS}
                  placeholder='e.g. Exit 5, in front of the ticket gate'
                />
              </label>
              <label>
                <span className={LABEL_CLASS}>Listing area</span>
                <input
                  name='location'
                  value={form.location}
                  onChange={updateField}
                  className={INPUT_CLASS}
                  placeholder='e.g. Jongno-gu, Seoul'
                />
                {errors.location && (
                  <span className='mt-1 block typo-b12 text-red-600'>
                    {errors.location}
                  </span>
                )}
              </label>
            </div>
          )}
        </section>

        <section
          data-validation-field='dateSlots'
          className='rounded-xl bg-white p-8'
        >
          <div className='mb-6 flex items-center justify-between'>
            <h2 className='typo-title2 text-text-primary'>Booking slots *</h2>
            <BaseButton
              type='text'
              size='m'
              htmlType='button'
              hideIcon
              typo={false}
              onClick={addSlot}
              className='h-auto min-w-0 rounded-lg border border-green-deep02 bg-transparent px-4 py-2 typo-b14Strong text-green-deep02 hover:bg-green-light01 hover:text-green-deep02'
            >
              + Add slot
            </BaseButton>
          </div>
          <div className='flex flex-col gap-4'>
            {dateSlots.map((slot, index) => (
              <div
                key={index}
                className='grid grid-cols-[1.3fr_1fr_1fr_1fr_auto] items-end gap-3 rounded-lg bg-grayscale-gray01 p-4'
              >
                <label>
                  <span className={LABEL_CLASS}>Date</span>
                  <input
                    type='date'
                    value={slot.date}
                    onChange={(event) =>
                      updateSlot(index, 'date', event.target.value)
                    }
                    className={INPUT_CLASS}
                  />
                </label>
                <label>
                  <span className={LABEL_CLASS}>Start</span>
                  <input
                    type='time'
                    value={slot.startTime}
                    onChange={(event) =>
                      updateSlot(index, 'startTime', event.target.value)
                    }
                    className={INPUT_CLASS}
                  />
                </label>
                <label>
                  <span className={LABEL_CLASS}>End</span>
                  <input
                    type='time'
                    value={slot.endTime}
                    onChange={(event) =>
                      updateSlot(index, 'endTime', event.target.value)
                    }
                    className={INPUT_CLASS}
                  />
                </label>
                <label>
                  <span className={LABEL_CLASS}>Capacity</span>
                  <input
                    type='number'
                    min='1'
                    value={slot.maxParticipants}
                    onChange={(event) =>
                      updateSlot(
                        index,
                        'maxParticipants',
                        event.target.value,
                      )
                    }
                    className={INPUT_CLASS}
                  />
                </label>
                <BaseButton
                  type='text'
                  size='m'
                  htmlType='button'
                  hideIcon
                  typo={false}
                  onClick={() => removeSlot(index)}
                  isDisabled={dateSlots.length === 1}
                  disabledTextClass='text-red-600'
                  aria-label={`Remove slot ${index + 1}`}
                  className='mb-1 h-[44px] min-w-0 rounded-lg border-0 bg-transparent px-3 typo-b14Strong text-red-600 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30'
                >
                  Remove
                </BaseButton>
              </div>
            ))}
          </div>
          {errors.dateSlots && (
            <p className='mt-3 typo-b12 text-red-600'>{errors.dateSlots}</p>
          )}
        </section>

        {/* 예약 확인 전에 참가자에게 물어볼 것 — 알러지처럼 미리 알아야 하는 정보 */}
        <section
          data-validation-field='questions'
          className='rounded-xl bg-white p-8'
        >
          <div className='mb-2 flex items-center justify-between'>
            <h2 className='typo-title2 text-text-primary'>
              Pre-booking questions
            </h2>
            <BaseButton
              type='text'
              size='m'
              htmlType='button'
              hideIcon
              typo={false}
              onClick={addQuestion}
              className='h-auto min-w-0 rounded-lg border border-green-deep02 bg-transparent px-4 py-2 typo-b14Strong text-green-deep02 hover:bg-green-light01 hover:text-green-deep02'
            >
              + Add question
            </BaseButton>
          </div>
          <p className='mb-6 typo-b13 text-text-placeholder'>
            Guests answer these in a pop-up before they can confirm a booking.
            Leave empty to skip. Answers appear next to each guest in the
            participant list.
          </p>

          {questions.length === 0 ? (
            <p className='rounded-lg bg-grayscale-gray01 px-4 py-6 text-center typo-b14 text-text-placeholder'>
              No questions yet. Guests book without any extra step.
            </p>
          ) : (
            <div className='flex flex-col gap-4'>
              {questions.map((question, index) => (
                <div
                  key={question.id ?? `new-${index}`}
                  className='rounded-lg bg-grayscale-gray01 p-4'
                >
                  <div className='grid grid-cols-[1fr_180px_auto] items-end gap-3'>
                    <label>
                      <span className={LABEL_CLASS}>Question *</span>
                      <input
                        type='text'
                        maxLength={200}
                        value={question.label}
                        placeholder='e.g. Do you have any food allergies?'
                        onChange={(event) =>
                          updateQuestion(index, 'label', event.target.value)
                        }
                        className={INPUT_CLASS}
                      />
                    </label>
                    <label>
                      <span className={LABEL_CLASS}>Answer type</span>
                      <select
                        value={question.type}
                        onChange={(event) =>
                          updateQuestion(index, 'type', event.target.value)
                        }
                        className={INPUT_CLASS}
                      >
                        {QUESTION_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <BaseButton
                      type='text'
                      size='m'
                      htmlType='button'
                      hideIcon
                      typo={false}
                      onClick={() => removeQuestion(index)}
                      aria-label={`Remove question ${index + 1}`}
                      className='mb-1 h-[44px] min-w-0 rounded-lg border-0 bg-transparent px-3 typo-b14Strong text-red-600 hover:bg-red-50 hover:text-red-600'
                    >
                      Remove
                    </BaseButton>
                  </div>

                  {question.type === 'CHOICE' && (
                    <label className='mt-3 block'>
                      <span className={LABEL_CLASS}>
                        Options (one per line) *
                      </span>
                      <textarea
                        rows={3}
                        value={question.optionsText}
                        placeholder={'Vegetarian\nVegan\nNo restrictions'}
                        onChange={(event) =>
                          updateQuestion(
                            index,
                            'optionsText',
                            event.target.value,
                          )
                        }
                        className={`${INPUT_CLASS} resize-y`}
                      />
                    </label>
                  )}

                  <label className='mt-3 flex items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={question.required}
                      onChange={(event) =>
                        updateQuestion(index, 'required', event.target.checked)
                      }
                      className='h-4 w-4 accent-green-deep02'
                    />
                    <span className='typo-b14 text-text-primary'>
                      Required — guests cannot book without answering
                    </span>
                  </label>
                </div>
              ))}
            </div>
          )}

          {errors.questions && (
            <p className='mt-3 typo-b12 text-red-600'>{errors.questions}</p>
          )}
        </section>

        <section className='rounded-xl bg-white p-8'>
          <h2 className='mb-6 typo-title2 text-text-primary'>
            Price & visibility
          </h2>
          <div className='grid grid-cols-2 gap-5'>
            <label>
              <span className={LABEL_CLASS}>Currency</span>
              <select
                name='currency'
                value={form.currency}
                onChange={updateField}
                disabled={form.free}
                className={`${INPUT_CLASS} disabled:bg-grayscale-gray02`}
              >
                <option value='USD'>USD</option>
                <option value='KRW'>KRW</option>
              </select>
            </label>
            <label>
              <span className={LABEL_CLASS}>Price *</span>
              <input
                data-validation-field='price'
                name='price'
                type='number'
                min='0'
                step={form.currency === 'KRW' ? '1' : '0.01'}
                value={form.free ? '0' : form.price}
                onChange={updateField}
                disabled={form.free}
                className={`${INPUT_CLASS} disabled:bg-grayscale-gray02`}
                placeholder='0.00'
              />
              {errors.price && (
                <span className='mt-1 block typo-b12 text-red-600'>
                  {errors.price}
                </span>
              )}
            </label>
          </div>
          <div className='mt-6 flex flex-wrap gap-6'>
            {[
              ['free', 'Free workshop'],
              ['featured', 'Featured'],
              ['hit', 'Popular / Hit'],
            ].map(([name, label]) => (
              <label
                key={name}
                className='flex cursor-pointer items-center gap-2 typo-b14 text-text-primary'
              >
                <input
                  type='checkbox'
                  name={name}
                  checked={form[name]}
                  onChange={updateField}
                  className='size-4 accent-green-deep02'
                />
                {label}
              </label>
            ))}
          </div>
        </section>

        {/* 결제 방식 — 계좌이체 워크숍(예: Prana Club 한옥 웰니스)만 BANK_TRANSFER 로 켠다 */}
        <section
          data-validation-field='bankTransfer'
          className='rounded-xl bg-white p-8'
        >
          <h2 className='mb-2 typo-title2 text-text-primary'>Payment method</h2>
          <p className='mb-6 typo-b13 text-text-placeholder'>
            Card (Eximbay) is the default. Choose bank transfer only when the
            organizer collects the fee directly: guests get the account details on
            screen and by email, the spot is held for 3 hours, and an admin
            confirms the booking after the organizer verifies the deposit.
          </p>
          <div className='grid grid-cols-2 gap-5'>
            <label>
              <span className={LABEL_CLASS}>Payment method</span>
              <select
                name='paymentMethod'
                value={form.paymentMethod}
                onChange={updateField}
                disabled={form.free}
                className={`${INPUT_CLASS} disabled:bg-grayscale-gray02`}
              >
                <option value='CARD'>Card · Eximbay</option>
                <option value='BANK_TRANSFER'>
                  Bank transfer · manual confirmation
                </option>
              </select>
            </label>
            <label>
              <span className={LABEL_CLASS}>
                Organizer notification e-mail
              </span>
              <input
                data-validation-field='hostNotificationEmail'
                name='hostNotificationEmail'
                type='email'
                value={form.hostNotificationEmail}
                onChange={updateField}
                className={INPUT_CLASS}
                placeholder='organizer@example.com'
              />
              <span className='mt-1 block typo-b12 text-text-placeholder'>
                Gets the guest&apos;s Koready ID, session and time on every
                bank-transfer booking. Leave empty to skip.
              </span>
              {errors.hostNotificationEmail && (
                <span className='mt-1 block typo-b12 text-red-600'>
                  {errors.hostNotificationEmail}
                </span>
              )}
            </label>
          </div>

          {form.paymentMethod === 'BANK_TRANSFER' && !form.free && (
            <>
              <div className='mt-5 grid grid-cols-3 gap-5'>
                <label>
                  <span className={LABEL_CLASS}>Bank *</span>
                  <input
                    name='bankName'
                    value={form.bankName}
                    onChange={updateField}
                    className={INPUT_CLASS}
                    placeholder='Kbank 케이뱅크'
                  />
                </label>
                <label>
                  <span className={LABEL_CLASS}>Account number *</span>
                  <input
                    name='bankAccountNumber'
                    value={form.bankAccountNumber}
                    onChange={updateField}
                    className={INPUT_CLASS}
                    placeholder='100167570102'
                  />
                </label>
                <label>
                  <span className={LABEL_CLASS}>Account holder *</span>
                  <input
                    name='bankAccountHolder'
                    value={form.bankAccountHolder}
                    onChange={updateField}
                    className={INPUT_CLASS}
                    placeholder='황지희'
                  />
                </label>
              </div>
              {errors.bankTransfer && (
                <p className='mt-3 typo-b12 text-red-600'>{errors.bankTransfer}</p>
              )}
              <label className='mt-5 block'>
                <span className={LABEL_CLASS}>
                  Confirmation e-mail message
                </span>
                <textarea
                  name='confirmationMessage'
                  value={form.confirmationMessage}
                  onChange={updateField}
                  className={`${INPUT_CLASS} min-h-[80px] resize-y`}
                  placeholder="We'll see you at a beautiful hanok in Suwon. Come ready to relax and meet new people."
                />
                <span className='mt-1 block typo-b12 text-text-placeholder'>
                  Shown under &quot;Your booking is confirmed 🧘‍♀️&quot; in the
                  e-mail sent when an admin confirms the transfer.
                </span>
              </label>
            </>
          )}
        </section>

        <section
          data-validation-field='images'
          className='rounded-xl bg-white p-8'
        >
          <h2 className='mb-6 typo-title2 text-text-primary'>
            Images & discovery
          </h2>
          <WorkshopImageUploader
            images={images}
            onChange={(nextImages) => {
              setImages(nextImages);
              setErrors((prev) => ({ ...prev, images: '' }));
            }}
            error={errors.images}
            disabled={submitting}
          />
          <label className='mt-5 block'>
            <span className={LABEL_CLASS}>Keywords</span>
            <input
              name='keywordsText'
              value={form.keywordsText}
              onChange={updateField}
              className={INPUT_CLASS}
              placeholder='culture, food, beginner'
            />
          </label>
        </section>

        <section className='rounded-xl bg-white p-8'>
          <h2 className='mb-6 typo-title2 text-text-primary'>
            Guest information
          </h2>
          <label className='mb-5 block'>
            <span className={LABEL_CLASS}>Things to know</span>
            <textarea
              name='thingsToKnow'
              value={form.thingsToKnow}
              onChange={updateField}
              className={`${INPUT_CLASS} min-h-[100px] resize-y`}
              placeholder='What guests should prepare'
            />
          </label>
          <div className='grid grid-cols-3 gap-5'>
            <label>
              <span className={LABEL_CLASS}>Number</span>
              <input
                name='inquiryPhone'
                type='tel'
                value={form.inquiryPhone}
                onChange={updateField}
                className={INPUT_CLASS}
                placeholder='+82 10-1234-5678'
              />
            </label>
            <label>
              <span className={LABEL_CLASS}>E-mail</span>
              <input
                data-validation-field='inquiryEmail'
                name='inquiryEmail'
                type='email'
                value={form.inquiryEmail}
                onChange={updateField}
                className={INPUT_CLASS}
                placeholder='contact@example.com'
              />
              {errors.inquiryEmail && (
                <span className='mt-1 block typo-b12 text-red-600'>
                  {errors.inquiryEmail}
                </span>
              )}
            </label>
            <label>
              <span className={LABEL_CLASS}>Open chat ID</span>
              <input
                name='inquiryOpenChatId'
                value={form.inquiryOpenChatId}
                onChange={updateField}
                className={INPUT_CLASS}
                placeholder='Kakao open chat ID'
              />
            </label>
          </div>
          <label className='mt-5 block'>
            <span className={LABEL_CLASS}>Additional inquiry information</span>
            <textarea
              name='inquiry'
              value={form.inquiry}
              onChange={updateField}
              className={`${INPUT_CLASS} min-h-[80px] resize-y`}
              placeholder='Optional contact notes'
            />
          </label>
        </section>

        {/* "View cancellation & refund details" — 토글이 꺼져 있으면 기본 Koready 정책, 켜면 이 워크숍 전용 문구 */}
        <section
          data-validation-field='refundPolicy'
          className='rounded-xl bg-white p-8'
        >
          <div className='mb-2 flex items-center justify-between gap-4'>
            <h2 className='typo-title2 text-text-primary'>
              View cancellation &amp; refund details
            </h2>
            <ToggleButton
              size='m'
              togglePosition='right'
              value={form.useCustomRefundPolicy}
              onChange={(next) => {
                setForm((prev) => ({ ...prev, useCustomRefundPolicy: next }));
                setErrors((prev) => ({ ...prev, refundPolicy: '' }));
              }}
              content={
                form.useCustomRefundPolicy
                  ? 'Custom policy for this workshop'
                  : 'Using the default Koready policy'
              }
            />
          </div>
          <p className='mb-6 typo-b13 text-text-placeholder'>
            This is what guests see when they open &quot;View cancellation &amp;
            refund details&quot; in My bag, Check details, checkout and the
            booking confirmation, and in the Cancellation &amp; Refund tab on
            the workshop page. Turn the toggle on to write a policy for this
            workshop only. Off keeps the default Koready wording.
          </p>

          <div
            className={`grid gap-5 ${
              form.useCustomRefundPolicy ? 'grid-cols-2' : 'grid-cols-1'
            }`}
          >
            {form.useCustomRefundPolicy && (
              <label className='block'>
                <span className={LABEL_CLASS}>Policy text *</span>
                <textarea
                  name='refundPolicy'
                  value={form.refundPolicy}
                  onChange={updateField}
                  className={`${INPUT_CLASS} min-h-[420px] resize-y font-mono text-[13px]`}
                  placeholder={
                    '# Refund and cancellation policy\n\n## Refund policy\nRefunds depend on when you cancel your booking.\n\n- **7 or more days before the event:** Full refund\n- **3–6 days before the event:** 50% refund\n- **2 days or less before the event:** No refund\n\n## How to request a refund\n1. Go to My Page → Payments → View invoice → Request a refund.\n2. Select your reason and submit.'
                  }
                />
                <span className='mt-1 block typo-b12 text-text-placeholder'>
                  Supports headings (#, ##), bullet lists (-), numbered lists
                  (1.) and **bold**. Plain text works too.
                </span>
                {errors.refundPolicy && (
                  <span className='mt-1 block typo-b12 text-red-600'>
                    {errors.refundPolicy}
                  </span>
                )}
              </label>
            )}

            <div className='block'>
              <span className={LABEL_CLASS}>
                Preview · what guests will see
              </span>
              <div className='rounded-lg border border-grayscale-gray02 bg-grayscale-gray01 p-6 min-h-[200px] max-h-[520px] overflow-auto'>
                {form.useCustomRefundPolicy && !form.refundPolicy.trim() ? (
                  <p className='typo-b14 text-text-placeholder'>
                    Start typing on the left to see the preview.
                  </p>
                ) : (
                  <RefundPolicyContent
                    content={
                      form.useCustomRefundPolicy ? form.refundPolicy : ''
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        <footer className='sticky bottom-0 z-10 flex justify-end gap-3 rounded-xl border border-grayscale-gray02 bg-white p-5 shadow-lg'>
          <BaseButton
            type='text'
            size='m'
            htmlType='button'
            hideIcon
            typo={false}
            onClick={handleCancel}
            isDisabled={submitting}
            disabledTextClass='text-text-primary'
            disabledBorderClass='border-grayscale-gray03'
            className='h-auto min-w-0 rounded-lg border border-grayscale-gray03 bg-transparent px-6 py-3 typo-b14Strong text-text-primary hover:text-text-primary disabled:opacity-50'
          >
            Cancel
          </BaseButton>
          <BaseButton
            type='primary'
            size='m'
            htmlType='submit'
            hideIcon
            typo={false}
            isDisabled={submitting}
            disabledBgClass='bg-green-deep02'
            className='min-w-[160px] h-auto rounded-lg border-0 bg-green-deep02 px-6 py-3 typo-b14Strong text-white hover:bg-green-deep02 hover:opacity-90 disabled:cursor-wait disabled:opacity-50'
          >
            {submitting
              ? isEditMode
                ? 'Saving...'
                : 'Creating...'
              : isEditMode
                ? 'Save changes'
                : 'Create workshop'}
          </BaseButton>
        </footer>
      </form>
    </main>
  );
};

export default WorkshopCreate;
